import { ToggleButton, ToggleButtonGroup, Stack, FormControlLabel, Checkbox, Typography, TextField, FormHelperText } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import moment from 'moment/moment'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'

const CalendarRepeatComponent = (props) => {
  const { rrule, onChange, showErrors, minDate, disable, isJobLinkerEvent = false, calendarEvent } = props
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const weekdays = [
    { key: 'MO', value: t_('Monday') },
    { key: 'TU', value: t_('Tuesday') },
    { key: 'WE', value: t_('Wednesday') },
    { key: 'TH', value: t_('Thursday') },
    { key: 'FR', value: t_('Friday') },
    { key: 'SA', value: t_('Saturday') },
    { key: 'SU', value: t_('Sunday') }
  ]
  const intervalError = (showErrors && rrule.INTERVAL < 1)
  const untilError = (showErrors && minDate >= rrule.UNTIL)
  const weekDayError = (showErrors && rrule.BYDAY.length === 0)

  if (isJobLinkerEvent) {
    rrule.ON = true
  }

  return (
    <>
      <Stack direction='row' alignItems='baseline' justifyContent='center' spacing={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isJobLinkerEvent ? true : rrule.ON}
              onChange={(event) => {
                onChange({
                  ...rrule,
                  ON: event.target.checked,
                  UNTIL: minDate > rrule.UNTIL ? minDate : rrule.UNTIL
                })
              }}
              disabled={isJobLinkerEvent || disable}
            />
          }
          label={rrule.ON ? t_('Repeat every') : t_('Repeat')}
        />
        {rrule.ON
          ? (
            <>
              <TextField
                label=''
                variant='outlined'
                type='number'
                sx={{ width: '5rem' }}
                value={rrule.INTERVAL}
                error={intervalError}
                helperText={intervalError ? t_('Must be bigger than 0') : null}
                onChange={(event) => {
                  if (isJobLinkerEvent) {
                    const now = moment()
                    const updatedTime = now.toDate()
                    if ((calendarEvent.startDate === null || calendarEvent.startDate === undefined || moment(calendarEvent.startDate).isValid() === false)) {
                      onChange({
                        ...rrule,
                        INTERVAL: event.target.value
                      }, updatedTime)
                    } else {
                      onChange({ ...rrule, INTERVAL: event.target.value })
                    }
                  } else {
                    onChange({ ...rrule, INTERVAL: event.target.value })
                  }
                }}
                disabled={disable}
              />
              <Typography>{t_('week')}{rrule.INTERVAL > 1 ? 's' : null}</Typography>
              {!isJobLinkerEvent && (
                <Typography>{t_('until')}</Typography>
              )}
              {!isJobLinkerEvent
                ? (
                  <DatePicker
                    label=''
                    value={moment.isMoment(rrule.UNTIL) ? rrule.UNTIL.toDate() : rrule.UNTIL}
                    onChange={(newValue) => {
                      onChange({ ...rrule, UNTIL: newValue })
                    }}
                    slotProps={{ textField: { error: untilError, helperText: untilError ? t_('Must be later than end date') : null } }}
                    disabled={disable}
                    format={format.date}
                  />
                  )
                : null}
            </>)
          : null}
      </Stack>
      {rrule.ON
        ? (
          <>
            <ToggleButtonGroup
              color='primary'
              value={rrule.BYDAY}
              onChange={(event, newValue) => {
                if (isJobLinkerEvent) {
                  const now = moment()
                  const updatedTime = now.toDate()
                  if (calendarEvent.startDate === null || calendarEvent.startDate === undefined || moment(calendarEvent.startDate).isValid() === false) {
                    onChange({
                      ...rrule,
                      BYDAY: newValue
                    }, updatedTime)
                  } else {
                    onChange({ ...rrule, BYDAY: newValue })
                  }
                } else {
                  onChange({ ...rrule, BYDAY: newValue })
                }
              }}
              exclusive={false}
              aria-label={t_('Repeat week days')}
              disabled={disable}
            >
              {weekdays.map((day) => {
                return (
                  <ToggleButton
                    key={day.key}
                    value={day.key}
                    sx={weekDayError ? { borderColor: (theme) => theme.palette.error.main } : null}
                    disabled={disable}
                  >
                    {day.value}
                  </ToggleButton>
                )
              })}
            </ToggleButtonGroup>

            {weekDayError ? <FormHelperText color='common.error' error={showErrors}>{t_('You must select at least one week day')}</FormHelperText> : null}
          </>)

        : null}
    </>
  )
}

// Convert RRule string to javascript object
// Example: FREQ=WEEKLY;BYDAY=SU,TU;INTERVAL=1
const rruleStringToObject = (rrule, endDate) => {
  if (rrule === null || rrule === undefined) {
    // Return default values
    return { FREQ: 'WEEKLY', BYDAY: [], INTERVAL: 1, ON: false, UNTIL: endDate || new Date() }
  }
  const rruleParams = {}
  const rruleSplit = rrule.split(';')
  rruleSplit.forEach(element => {
    let [key, value] = element.split('=')
    if (value.indexOf(',') >= 0 || key === 'BYDAY') {
      value = value.split(',')
    }
    rruleParams[key] = value
  })
  rruleParams.ON = true
  rruleParams.UNTIL = moment(rruleParams.UNTIL, 'YYYYMMDD[T]hhmmss[Z]')
  return rruleParams
}

const rruleObjectToString = (rrule) => {
  if (rrule === null || !rrule.ON) {
    return null
  }
  const rruleValues = []
  for (const key in rrule) {
    if (key !== 'ON') {
      let value = rrule[key]
      if (key === 'UNTIL') {
        value = moment(value).format('YYYYMMDD[T000000Z]')
      } else if (Array.isArray(value)) {
        value = value.join()
      }
      rruleValues.push(key + '=' + value)
    }
  }
  return rruleValues.join(';')
}

const isRRuleValid = (rrule, minDate) => {
  if (rrule.INTERVAL < 1 || minDate >= rrule.UNTIL || rrule.BYDAY.length === 0) {
    return false
  } else {
    return true
  }
}

export { CalendarRepeatComponent, rruleStringToObject, rruleObjectToString, isRRuleValid }
